import { Injectable } from '@angular/core';

import * as uuid from 'uuid';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import $ from 'jquery';
import { ExcelTemplate } from './excel.model';
import { ConfigurationService } from '../configuration/configuration.service';
import { NotifyService } from '../notify/notify.service';

type AOA = Array<Array<any>>;

@Injectable({
  providedIn: 'root'
})
export class ExcelService {
  importId = 'I' + uuid.v4();
  private get configuration() {
    return this.configurationService.getExcelConfiguration();
  }
  constructor(
    private configurationService: ConfigurationService,
    private notifyService: NotifyService) {
  }

  import = () => {
    return new Promise<{ items: any[], file: File }>((resolve, reject) => {
      if ($(`#${this.importId}`).length) {
        $('body').children(`#${this.importId}`).remove();
      }
      $('body').append(`<input id="${this.importId}" style="display: none" type="file" />`);

      $(`#${this.importId}`).on('change', (evt: any) => {
        try {
          const target: DataTransfer = evt.target;
          if (target.files.length !== 1) {
            throw new Error('Cannot use multiple files');
          }
          const reader: FileReader = new FileReader();
          reader.onload = (e: any) => {
            const bstr: string = e.target.result;
            const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });
            const wsname: string = wb.SheetNames[0];
            const ws: XLSX.WorkSheet = wb.Sheets[wsname];
            const lines: AOA = (XLSX.utils.sheet_to_json(ws, { header: 1 }));
            const records = [];
            const headers = lines[0];
            lines.splice(0, 1);
            lines.forEach(line => {
              const record = {};
              let hasValue = false;
              headers.forEach((header, index) => {
                record[header] = line[index];
                if (line[index] !== undefined && line[index] !== null && line[index].toString()) {
                  hasValue = true;
                }
                if (typeof (record[header]) === 'string') {
                  record[header] = record[header].trim();
                }
                if (record[header] === '') {
                  record[header] = null;
                }
              });
              if (hasValue) {
                records.push(record);
              }
            });
            resolve({
              items: records,
              file: target.files[0]
            });
          };
          reader.readAsBinaryString(target.files[0]);
        } catch (error) {
          reject(error);
        }
      });
      $(`#${this.importId}`).trigger('click');
    });
  }

  export = (exportInfo: IExportInfo) => {
    /* Worksheet */
    const worksheet = XLSX.utils.json_to_sheet(exportInfo.data, { cellDates: true });
    /* Workbook */
    const workbook = XLSX.utils.book_new();
    /* Add worksheet to workbook */
    XLSX.utils.book_append_sheet(workbook, worksheet, exportInfo.sheetName || 'Default');

    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });
    const fileName = exportInfo.fileName ? exportInfo.fileName + this.current() : uuid.v4() + '.xlsx';
    saveAs(new Blob([this.s2ab(wbout)], { type: 'application/octet-stream' }), fileName);
  }

  private current = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hour = now.getHours().toString().padStart(2, '0');
    const minute = now.getMinutes().toString().padStart(2, '0');
    const second = now.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  }

  private s2ab = (s: any): ArrayBuffer => {
    const buffer = new ArrayBuffer(s.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < s.length; ++i) {
      // tslint:disable-next-line: no-bitwise
      view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buffer;
  }

  generateExcelTemplate = async (excelTemplate: ExcelTemplate) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    return await this.configuration.generateTemplate(excelTemplate).catch(err => {
      this.notifyService.notify.error(err);
      return {
        fileName: '',
        filePath: ''
      };
    });
  }

  generateAndUploadExcelTemplate = async (key: string, excelTemplate: ExcelTemplate) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    return await this.configuration.generateAndUploadTemplate(key, excelTemplate).catch(err => {
      this.notifyService.notify.error(err);
      return {
        fileName: '',
        filePath: ''
      };
    });
  }

  uploadTemplate = async (key?: string, validator?: (fileName: string) => string) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    return await this.configuration.uploadTemplate(key, validator);
  }

  downloadTemplate = async (filePath: string, fileName?: string) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    await this.configuration.downloadTemplate(filePath, fileName);
  }

  generateExcelDataByTemplate = async (filePath: string, columns: any[], items: any[], fileName?: string) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    const result = await this.configuration.generateExcelDataByTemplate(filePath, columns, items).catch(err => {
      this.notifyService.notify.error(err);
      return {
        filePath: ''
      };
    });
    if (result?.filePath) {
      await this.configuration.downloadTemplate(result.filePath, fileName || `${uuid.v4()}.xlsx`);
    }
  }

  getFiles = async (key?: string) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    return await this.configuration.getFiles(key);
  }

  remove = async (key?: string, name?: string) => {
    if (!this.configuration) {
      this.notifyService.notify.warning('Please init excel configuration');
      return;
    }
    return await this.configuration.remove(key, name);
  }
}

export interface IExportInfo {
  data: any[];
  sheetName?: string;
  fileName?: string;
}
